import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  styled,
  Chip,
  Modal,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Autocomplete,
  Stack,
  Snackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  EmailOutlined as EmailIcon,
  PhoneAndroid as PhoneIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  DeleteForever as DeleteForeverIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Work as WorkIcon,
  AssignmentInd as AssignmentIndIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CorporateFare as CorporateFareIcon,
  CalendarToday as CalendarTodayIcon,
  Home as HomeIcon,
  ContactPhone as ContactPhoneIcon,
  Notes as NotesIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PersonnelBranchTransferModal from './PersonnelBranchTransferModal';
import { getPersonnelBalances, type PersonnelBalance } from '../../services/migration/personnelTransactionService';
import { cardManagementService } from '../../services/cardManagementService';
import firebaseDataService from '../../services/FirebaseDataService';
import {
  getPersonnelByUid,
  updatePersonnel,
  transferBranch as transferBranchSql,
  getDepartments,
  type PersonnelUpdateInput,
} from '../../services/migration/personnelDataService';
import { useAuth } from '../../contexts/AuthContext';
import { nodeApi } from '../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Modal için styled component
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowY: 'auto',
  padding: theme.spacing(2, 0),
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '90%',
  maxWidth: 1200,
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ModalSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

// Görev durumuna göre renk döndür
const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success.main';
    case 'started':
      return 'info.main';
    case 'accepted':
      return 'secondary.main';
    case 'pending':
    default:
      return 'warning.main';
  }
};

// Görev durumuna göre metin döndür
const getStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Tamamlandı';
    case 'started':
      return 'Görev Başladı';
    case 'accepted':
      return 'Kabul Edildi';
    case 'pending':
    default:
      return 'Beklemede';
  }
};

interface PersonnelDetailsModalProps {
  open: boolean;
  onClose: () => void;
  selectedPersonnel: any;
  personnelTasks: any[];
  loadingTasks: boolean;
  showDeleted: boolean;
  confirmDelete: boolean;
  onDeleteConfirm: () => void;
  onCancelDelete: () => void;
  onDeletePersonnel: () => void;
  isManager?: boolean;
  onPersonnelUpdated?: () => void;
  /** Hareketler butonu: verilmezse /personnel/actions'a gider, null ise buton gizlenir (yönetici paneli). */
  onOpenTransactions?: ((personnelId: string) => void) | null;
}

const PersonnelDetailsModal: React.FC<PersonnelDetailsModalProps> = ({
  open,
  onClose,
  selectedPersonnel,
  personnelTasks,
  loadingTasks,
  showDeleted,
  confirmDelete,
  onDeleteConfirm,
  onCancelDelete,
  onDeletePersonnel,
  isManager = false,
  onPersonnelUpdated,
  onOpenTransactions,
}) => {
  const { t } = useTranslation('humanResources');
  // ✅ AuthContext'ten getBranchName VE userDetails al - Firebase sorgusu YOK!
  const { getBranchName: getBranchNameFromContext, userDetails, getPersonnelInfo } = useAuth();
  const navigate = useNavigate();
  
  const [branchTransferModalOpen, setBranchTransferModalOpen] = useState(false);
  const [branchName, setBranchName] = useState<string>('');
  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);
  const [removingFromBranch, setRemovingFromBranch] = useState(false);
  const [branchRemoveSuccess, setBranchRemoveSuccess] = useState<string | null>(null);
  const [branchRemoveError, setBranchRemoveError] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [departmentName, setDepartmentName] = useState<string>('');
  const [loadingDepartment, setLoadingDepartment] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  
  // CompanyId için state ekleniyor
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loadingCompanyId, setLoadingCompanyId] = useState(false);
  // Personel verilerini tutan state ekleyelim
  const [currentCompanyData, setCurrentCompanyData] = useState<any>({});

  const [editableName, setEditableName] = useState('');
  const [editableEmail, setEditableEmail] = useState('');
  const [editablePhone, setEditablePhone] = useState('');
  const [editableDepartmentId, setEditableDepartmentId] = useState<string | null>(null);
  const [editableJobTitle, setEditableJobTitle] = useState('');
  const [editableStartDate, setEditableStartDate] = useState('');
  const [editableBirthDate, setEditableBirthDate] = useState('');
  const [editableAddress, setEditableAddress] = useState('');
  const [editableEmergencyContactName, setEditableEmergencyContactName] = useState('');
  const [editableEmergencyContactPhone, setEditableEmergencyContactPhone] = useState('');
  const [editableNotes, setEditableNotes] = useState('');

  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingDepartmentsForSelect, setLoadingDepartmentsForSelect] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Mali özet ve personele bağlı kartlar
  const [balances, setBalances] = useState<PersonnelBalance[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [personnelCards, setPersonnelCards] = useState<any[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);

  // CompanyId'yi users koleksiyonundan çek (Cloud Functions üzerinden)
  // ✅ CompanyId artık AuthContext cache'inden - Firebase sorgusu YOK!
  const fetchCompanyId = async (userId: string) => {
    if (!userId) return null;
    
    setLoadingCompanyId(true);
    try {
      // ✅ Önce AuthContext cache'inden dene
      if (userDetails?.companyId) {
        console.log('[PersonnelDetailsModal] CompanyId AuthContext cache\'inden alındı:', userDetails.companyId);
        setCompanyId(userDetails.companyId);
        setLoadingCompanyId(false);
        return userDetails.companyId;
      }
      
      // ✅ Personel cache'inden dene
      const personnelInfo = getPersonnelInfo(userId);
      if (personnelInfo?.companyId) {
        console.log('[PersonnelDetailsModal] CompanyId personel cache\'inden alındı:', personnelInfo.companyId);
        setCompanyId(personnelInfo.companyId);
        setLoadingCompanyId(false);
        return personnelInfo.companyId;
      }
      
      // Fallback: Cloud Functions
      console.log('[PersonnelDetailsModal] CompanyId Cloud Functions\'dan çekiliyor, userId:', userId);
      const userData = await firebaseDataService.getUserData(userId);
      
      if (userData) {
        const userCompanyId = userData.companyId || null;
        console.log('[PersonnelDetailsModal] CompanyId Cloud Functions\'dan alındı:', userCompanyId);
        setCompanyId(userCompanyId);
        return userCompanyId;
      } else {
        console.log('[PersonnelDetailsModal] User verisi bulunamadı');
        setCompanyId(null);
        return null;
      }
    } catch (error) {
      console.error('[PersonnelDetailsModal] CompanyId çekilirken hata:', error);
      setCompanyId(null);
      return null;
    } finally {
      setLoadingCompanyId(false);
    }
  };

  // Modal açıldığında önce companyId'yi çek
  useEffect(() => {
    if (open && selectedPersonnel?.id) {
      console.log('[PersonnelDetailsModal] Modal açıldı, selectedPersonnel:', selectedPersonnel);
      
      const fetchProfileImageUrl = async () => {
        setIsImageLoading(true);
        setProfileImageUrl(null);
        try {
          const response = await nodeApi.get(`/api/user-profile/${selectedPersonnel.id}/image-url`);
          if (response.data.success && response.data.imageUrl) {
            setProfileImageUrl(response.data.imageUrl);
          }
        } catch (error: any) {
          if (error.response?.status !== 404) {
            console.error('Profil resmi getirme hatası:', error);
          }
        } finally {
          setIsImageLoading(false);
        }
      };

      fetchProfileImageUrl();
      
      const getCompanyIdAndLoadData = async () => {
        // Eğer selectedPersonnel'da companyId varsa, direkt kullan
        if (selectedPersonnel.companyId) {
          console.log('[PersonnelDetailsModal] CompanyId zaten mevcut, kullanılıyor:', selectedPersonnel.companyId);
          setCompanyId(selectedPersonnel.companyId);
          loadDepartmentsForSelect(selectedPersonnel.companyId);
        } else {
          // CompanyId yoksa, users tablosundan çek
          const fetchedCompanyId = await fetchCompanyId(selectedPersonnel.id);
          
          if (fetchedCompanyId) {
            // CompanyId alındı, departmanları yükle
            console.log('[PersonnelDetailsModal] Departmanlar yükleniyor, companyId:', fetchedCompanyId);
            loadDepartmentsForSelect(fetchedCompanyId);
            
            // selectedPersonnel nesnesine companyId'yi ekle
            selectedPersonnel.companyId = fetchedCompanyId;
          }
        }
      };
      
      getCompanyIdAndLoadData();
    } else {
      setCompanyId(null);
    }
  }, [open, selectedPersonnel?.id]);

  // Mali özet ve personele bağlı kartlar. İkisi de bilgi amaçlıdır: hata durumunda
  // bölümler boş görünür, modalın geri kalanı etkilenmez.
  useEffect(() => {
    if (!open || !selectedPersonnel?.id) {
      setBalances([]);
      setPersonnelCards([]);
      return;
    }

    let cancelled = false;
    const uid = selectedPersonnel.id;

    const loadFinancials = async () => {
      setLoadingBalances(true);
      try {
        const result = await getPersonnelBalances(uid);
        if (!cancelled) setBalances(result);
      } catch {
        if (!cancelled) setBalances([]);
      } finally {
        if (!cancelled) setLoadingBalances(false);
      }
    };

    const loadCards = async () => {
      setLoadingCards(true);
      try {
        const res = await cardManagementService.getCardsByPersonnel(uid);
        if (!cancelled) setPersonnelCards(res.data?.data || []);
      } catch {
        if (!cancelled) setPersonnelCards([]);
      } finally {
        if (!cancelled) setLoadingCards(false);
      }
    };

    loadFinancials();
    loadCards();

    return () => { cancelled = true; };
  }, [open, selectedPersonnel?.id]);

  // Personelin şube bilgisini yükle (TEK SEFERLİK)
  useEffect(() => {
    const loadPersonnelBranch = async () => {
      if (open && selectedPersonnel?.id && selectedPersonnel.companyId) {
        console.log('[PersonnelDetailsModal] Personel şube bilgisi yükleniyor:', selectedPersonnel.id);
        
        try {
          const personnelData = await firebaseDataService.getPersonnel(selectedPersonnel.companyId, selectedPersonnel.id);
          
          if (personnelData && !Array.isArray(personnelData)) {
            const branchId = (personnelData as any).branchesId || null;
            
            console.log('[PersonnelDetailsModal] Personel şube bilgisi yüklendi:', branchId);
            
            // selectedPersonnel nesnesini güncelle
            if (selectedPersonnel) {
              selectedPersonnel.branchesId = branchId;
            }
            
            // Şube adını yükle
            if (branchId) {
              loadBranchName(branchId);
            } else {
              setBranchName('');
            }
          }
        } catch (error) {
          console.error('[PersonnelDetailsModal] Şube bilgisi yüklenirken hata:', error);
        }
      }
    };
    
    loadPersonnelBranch();
  }, [open, selectedPersonnel?.id, selectedPersonnel?.companyId]);

  const handleOpenBranchTransferModal = () => {
    setBranchTransferModalOpen(true);
  };

  const handleCloseBranchTransferModal = () => {
    setBranchTransferModalOpen(false);
    if (selectedPersonnel?.branchesId) {
      loadBranchName(selectedPersonnel.branchesId);
    }
    if (onPersonnelUpdated) onPersonnelUpdated();
  };

  const handleOpenRemoveDialog = () => {
    setConfirmRemoveDialogOpen(true);
  };

  const handleCloseRemoveDialog = () => {
    setConfirmRemoveDialogOpen(false);
  };

  const handleRemoveBranchAssignment = async () => {
    if (!selectedPersonnel || !selectedPersonnel.id || !selectedPersonnel.companyId) return;
    
    setRemovingFromBranch(true);
    setBranchRemoveError(null);
    setBranchRemoveSuccess(null);
    setConfirmRemoveDialogOpen(false);
    
    try {
      // Faz 3: sube atamasi kaldirma SQL'e (branchesId=null). RTDB'ye yazilmaz.
      await transferBranchSql(selectedPersonnel.id, null);
      
      setBranchRemoveSuccess(t('remaining.personnelDetailModal.messages.branchAssignmentRemoved', { branchName }));
      if (selectedPersonnel) selectedPersonnel.branchesId = null;
      setBranchName('');
      if (onPersonnelUpdated) onPersonnelUpdated();
      setTimeout(() => setBranchRemoveSuccess(null), 3000);
    } catch (error) {
      console.error('Şube ataması kaldırma hatası:', error);
      setBranchRemoveError(t('remaining.personnelDetailModal.messages.branchAssignmentRemoveError'));
    } finally {
      setRemovingFromBranch(false);
    }
  };

  const loadDepartmentsForSelect = async (companyId: string) => {
    console.log('[PersonnelDetailsModal] loadDepartmentsForSelect çağrıldı, companyId:', companyId);
    setLoadingDepartmentsForSelect(true);
    try {
      // Faz 3: departmanlar SQL'den (personnelDataService.getDepartments). id string'e cevrilir.
      const departmentsData = await getDepartments(companyId);
      if (departmentsData && departmentsData.length > 0) {
        const departmentsList = departmentsData.map((d) => ({
          id: String(d.id),
          name: d.name || t('remaining.personnelDetailModal.department.unnamed'),
        }));
        departmentsList.sort((a, b) => a.name.localeCompare(b.name));
        setDepartments(departmentsList);
      } else {
        setDepartments([]);
      }
    } catch (err) {
      console.error("[PersonnelDetailsModal] Departmanları yükleme hatası:", err);
      setDepartments([]);
    } finally {
      setLoadingDepartmentsForSelect(false);
    }
  };

  useEffect(() => {
    const loadPersonnelExtraData = async () => {
      if (!selectedPersonnel || !open) {
        console.log('[PersonnelDetailsModal] selectedPersonnel veya modal kapalı, temizleme yapılıyor');
        setBranchName('');
        setDepartmentName('');
        setIsEditMode(false);
        setEditableName('');
        setEditableEmail('');
        setEditablePhone('');
        setEditableDepartmentId(null);
        setEditableJobTitle('');
        setEditableStartDate('');
        setEditableBirthDate('');
        setEditableAddress('');
        setEditableEmergencyContactName('');
        setEditableEmergencyContactPhone('');
        setEditableNotes('');
        setCurrentCompanyData({}); // Personel verileri state'ini temizle
        return;
      }

      console.log('[PersonnelDetailsModal] loadPersonnelExtraData başladı, personnel:', selectedPersonnel);
      
      // Temel bilgileri ayarlama
      setEditableName(selectedPersonnel.name || '');
      setEditableEmail(selectedPersonnel.email || '');
      setEditablePhone(selectedPersonnel.phone || '');
      
      // Aktif companyId'yi al
      const activeCompanyId = selectedPersonnel.companyId || companyId;
      if (!activeCompanyId) {
        console.log('[PersonnelDetailsModal] CompanyId bulunamadı, verileri çekemiyoruz');
        return;
      }
      
      console.log('[PersonnelDetailsModal] Şirket personel verisi çekiliyor, yol:', `companies/${activeCompanyId}/personnel/${selectedPersonnel.id}`);
      const companyPersonnelData = await firebaseDataService.getPersonnel(activeCompanyId, selectedPersonnel.id);
      let companyData: any = {};
      if (companyPersonnelData && !Array.isArray(companyPersonnelData)) {
        companyData = companyPersonnelData;
        console.log('[PersonnelDetailsModal] Şirket personel verisi:', companyData);
        
        // Personel verilerini state'e kaydet
        setCurrentCompanyData(companyData);
        
        // Tüm personel bilgilerini detaylı log edelim
        console.log('[PersonnelDetailsModal] Yüklenen personel bilgileri:');
        console.log('- jobTitle:', companyData.jobTitle || 'Yok');
        console.log('- startDate:', companyData.startDate || 'Yok');
        console.log('- birthDate:', companyData.birthDate || 'Yok');
        console.log('- address:', companyData.address || 'Yok');
        console.log('- emergencyContactName:', companyData.emergencyContactName || 'Yok');
        console.log('- emergencyContactPhone:', companyData.emergencyContactPhone || 'Yok');
        console.log('- notes:', companyData.notes || 'Yok');
      } else {
        console.log('[PersonnelDetailsModal] Şirket personel verisi bulunamadı');
      }

      // Departman ID (SQL int) → string'e cevrilir (state string tutar).
      const departmentIdRaw = companyData.departmentId ?? null;
      const departmentId = departmentIdRaw != null ? String(departmentIdRaw) : null;
      console.log('[PersonnelDetailsModal] Companies altından bulunan departmentId:', departmentId);
      
      // Not: Departman ID'yi users tablosundan çekmiyoruz
      
      setEditableDepartmentId(departmentId);
      console.log('[PersonnelDetailsModal] Son departmentId:', departmentId);
      
      // Şube ID kontrolü, önce companies/personnel altından kontrol et
      let branchId = companyData.branchesId || selectedPersonnel.branchesId || null;
      console.log('[PersonnelDetailsModal] Bulunan ilk branchId:', branchId);
      
      // Şube ID yoksa kullanıcı veritabanından kontrol et (Cloud Functions üzerinden)
      if (!branchId) {
        console.log('[PersonnelDetailsModal] BranchId bulunamadı, users tablosundan kontrol ediliyor');
        const userData = await firebaseDataService.getUserData(selectedPersonnel.id);
        if (userData) {
          branchId = (userData as any).branchesId || null;
          console.log('[PersonnelDetailsModal] User tablosundan branchId:', branchId);
        }
      }

      // Şirket içi bilgiler
      console.log('[PersonnelDetailsModal] Şirket içi bilgileri state\'e atanıyor:');
      
      const jobTitle = companyData.jobTitle || '';
      console.log('jobTitle:', jobTitle);
      setEditableJobTitle(jobTitle);
      
      const startDate = companyData.startDate || '';
      console.log('startDate:', startDate);
      setEditableStartDate(startDate);
      
      // Kişisel bilgiler
      console.log('[PersonnelDetailsModal] Kişisel bilgiler state\'e atanıyor:');
      
      const birthDate = companyData.birthDate || '';
      console.log('birthDate:', birthDate);
      setEditableBirthDate(birthDate);
      
      const address = companyData.address || '';
      console.log('address:', address);
      setEditableAddress(address);
      
      // Acil durum bilgileri
      console.log('[PersonnelDetailsModal] Acil durum bilgileri state\'e atanıyor:');
      
      const emergencyContactName = companyData.emergencyContactName || '';
      console.log('emergencyContactName:', emergencyContactName);
      setEditableEmergencyContactName(emergencyContactName);
      
      const emergencyContactPhone = companyData.emergencyContactPhone || '';
      console.log('emergencyContactPhone:', emergencyContactPhone);
      setEditableEmergencyContactPhone(emergencyContactPhone);
      
      // Ek notlar
      console.log('[PersonnelDetailsModal] Ek notlar state\'e atanıyor:');
      
      const notes = companyData.notes || '';
      console.log('notes:', notes);
      setEditableNotes(notes);

      // Şube bilgisini yükle
      if (branchId) {
        console.log('[PersonnelDetailsModal] Şube bilgisi yükleniyor, branchId:', branchId);
        await loadBranchName(branchId);
      } else {
        console.log('[PersonnelDetailsModal] Şube bilgisi yüklenemedi, branchId yok');
        setBranchName('');
      }

      // Departman bilgisini yükle
      if (departmentId) {
        console.log('[PersonnelDetailsModal] Departman bilgisi yükleniyor, departmentId:', departmentId);
        await loadDepartmentName(departmentId);
      } else {
        console.log('[PersonnelDetailsModal] Departman bilgisi yüklenemedi, departmentId yok');
        setDepartmentName('');
      }
    };
    
    if (open && (selectedPersonnel?.companyId || companyId)) {
        loadPersonnelExtraData();
    }
  }, [open, selectedPersonnel, companyId]);

  // loadBranchName artık AuthContext'ten getBranchName kullanıyor
  const loadBranchName = (branchId: string) => {
    if (!branchId) {
      console.log('[PersonnelDetailsModal] loadBranchName - branchId eksik');
      setBranchName('');
      return;
    }
    const name = getBranchNameFromContext(branchId);
    console.log('[PersonnelDetailsModal] Şube adı AuthContext\'ten alındı:', name);
    setBranchName(name !== 'Şubeye Ait Değil' ? name : '');
  };

  const loadDepartmentName = async (departmentId: string) => {
    // Aktif companyId'yi al
    const activeCompanyId = selectedPersonnel?.companyId || companyId;

    if (!departmentId || !activeCompanyId) {
        console.log('[PersonnelDetailsModal] loadDepartmentName - departmentId veya companyId eksik');
        setDepartmentName('');
        return;
    }
    setLoadingDepartment(true);
    try {
      // Faz 3: departman adi SQL listesinden bulunur (personnelDataService.getDepartments).
      const allDepts = await getDepartments(activeCompanyId);
      const dept = allDepts.find((d) => String(d.id) === String(departmentId));
      if (dept) {
        setDepartmentName(dept.name || t('remaining.personnelPage.k010'));
      } else {
        setDepartmentName(t('remaining.personnelDetailModal.department.notFound'));
      }
    } catch (error) {
      console.error('Departman bilgisi yüklenirken hata:', error);
      setDepartmentName(t('remaining.personnelDetailModal.department.loadFailed'));
    } finally {
      setLoadingDepartment(false);
    }
  };

  const handleEditToggle = () => {
    console.log('[PersonnelDetailsModal] Düzenleme modu değişiyor, mevcut mod:', isEditMode);
    if (isEditMode) {
      console.log('[PersonnelDetailsModal] Düzenleme modundan çıkılıyor, verileri tekrar yüklüyoruz');
      if (selectedPersonnel && selectedPersonnel.companyId) {
        firebaseDataService.getPersonnel(selectedPersonnel.companyId, selectedPersonnel.id).then(personnelData => {
            if(personnelData && !Array.isArray(personnelData)){
                const companyData = personnelData as any;
                console.log('[PersonnelDetailsModal] Veritabanından personel verileri tekrar yüklendi:', companyData);
                
                // Personel verilerini state'e kaydet
                setCurrentCompanyData(companyData);
                
                // Düzenleme modundan çıkarken değerleri sıfırla
                const departmentIdRaw = companyData.departmentId ?? selectedPersonnel.departmentId ?? null;
                const departmentId = departmentIdRaw != null ? String(departmentIdRaw) : null;
                setEditableDepartmentId(departmentId);
                if (departmentId) {
                  loadDepartmentName(departmentId);
                } else {
                  setDepartmentName('');
                }
                
                // Şube bilgilerini güncelle
                const branchId = companyData.branchesId || selectedPersonnel.branchesId || null;
                if (branchId) {
                  loadBranchName(branchId);
                } else {
                  setBranchName('');
                }
                
                // Şirket içi, kişisel ve acil durum bilgilerini tekrar yükle
                console.log('[PersonnelDetailsModal] Diğer bilgiler tekrar yükleniyor:');
                console.log('- jobTitle:', companyData.jobTitle || 'Yok');
                console.log('- startDate:', companyData.startDate || 'Yok');
                console.log('- birthDate:', companyData.birthDate || 'Yok');
                console.log('- address:', companyData.address || 'Yok');
                console.log('- emergencyContactName:', companyData.emergencyContactName || 'Yok');
                console.log('- emergencyContactPhone:', companyData.emergencyContactPhone || 'Yok');
                console.log('- notes:', companyData.notes || 'Yok');
                
                setEditableJobTitle(companyData.jobTitle || '');
                setEditableStartDate(companyData.startDate || '');
                setEditableBirthDate(companyData.birthDate || '');
                setEditableAddress(companyData.address || '');
                setEditableEmergencyContactName(companyData.emergencyContactName || '');
                setEditableEmergencyContactPhone(companyData.emergencyContactPhone || '');
                setEditableNotes(companyData.notes || '');
            } else {
                console.log('[PersonnelDetailsModal] Düzenlemeden çıkarken personel verisi bulunamadı');
                setCurrentCompanyData({});
            }
        });
      }
    } else {
      // Düzenleme moduna geçildiğinde departmanları yükle
      if (selectedPersonnel?.companyId) {
        console.log('[PersonnelDetailsModal] Düzenleme moduna geçiliyor, departmanları yüklüyoruz');
        loadDepartmentsForSelect(selectedPersonnel.companyId);
      }
    }
    setIsEditMode(!isEditMode);
  };

  const handleSave = async () => {
    if (!selectedPersonnel || !selectedPersonnel.id) return;
    
    // Aktif companyId'yi al
    const activeCompanyId = selectedPersonnel.companyId || companyId;
    if (!activeCompanyId) {
      console.log('[PersonnelDetailsModal] CompanyId bulunamadı, kaydedilemedi');
      setSnackbarMessage(t('remaining.personnelDetailModal.messages.companyMissingSave'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSaveLoading(true);

    // Mevcut SQL kaydi (degisiklik tespiti icin)
    const personnelData = await getPersonnelByUid(selectedPersonnel.id, activeCompanyId);
    const current: any = personnelData || {};

    const nameParts = editableName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Faz 3: SQL update payload. Gonderilmeyen alan DEGISMEZ; metinlerde "" => temizle (null).
    const payload: PersonnelUpdateInput = {};

    const currentName = (current.name || `${current.firstName ?? ''} ${current.lastName ?? ''}`.trim() || selectedPersonnel.name || '').trim();
    if (editableName.trim() !== currentName) {
      payload.firstName = firstName;
      payload.lastName = lastName;
    }

    // NOT: email SQL update ucunda DESTEKLENMIYOR (Firebase Auth ile esitlenmesi gerekir) → bu modalda kaydedilmez.

    if (editablePhone.trim() !== (current.phone || '')) {
      payload.phone = editablePhone.trim();
    }

    // Departman (SQL int). editableDepartmentId string tutulur. 0 => temizle, >0 => ata.
    const currentDeptId = current.departmentId != null ? String(current.departmentId) : null;
    if ((editableDepartmentId || null) !== currentDeptId) {
      payload.departmentId = editableDepartmentId ? Number(editableDepartmentId) : 0;
    }

    // Genisletilmis HR alanlari ("" => temizle)
    if (editableJobTitle.trim() !== (current.jobTitle || '')) payload.jobTitle = editableJobTitle.trim();
    if (editableStartDate !== (current.startDate || '')) payload.startDate = editableStartDate || '';
    if (editableBirthDate !== (current.birthDate || '')) payload.birthDate = editableBirthDate || '';
    if (editableAddress.trim() !== (current.address || '')) payload.address = editableAddress.trim();
    if (editableEmergencyContactName.trim() !== (current.emergencyContactName || '')) payload.emergencyContactName = editableEmergencyContactName.trim();
    if (editableEmergencyContactPhone.trim() !== (current.emergencyContactPhone || '')) payload.emergencyContactPhone = editableEmergencyContactPhone.trim();
    if (editableNotes.trim() !== (current.notes || '')) payload.notes = editableNotes.trim();

    try {
      if (Object.keys(payload).length > 0) {
        // Faz 3: personel guncelleme SQL'e (personnelDataService.updatePersonnel). RTDB'ye yazilmaz.
        await updatePersonnel(selectedPersonnel.id, payload);
      } else {
        console.log('[PersonnelDetailsModal] Değişiklik yok, güncelleme yapılmadı');
      }

      // selectedPersonnel nesnesini yerel guncelle
      if (payload.firstName !== undefined) selectedPersonnel.name = editableName.trim();
      if (payload.phone !== undefined) selectedPersonnel.phone = editablePhone.trim();
      if (payload.departmentId !== undefined) {
        selectedPersonnel.departmentId = editableDepartmentId ? Number(editableDepartmentId) : null;
      }

      // Departman adını yükle
      if (editableDepartmentId) {
        await loadDepartmentName(editableDepartmentId);
      } else {
        setDepartmentName('');
      }

      setSnackbarMessage(t('remaining.personnelDetailModal.messages.personnelUpdated'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsEditMode(false);
      if (onPersonnelUpdated) onPersonnelUpdated();
    } catch (error) {
      console.error("Personel bilgileri güncellenirken hata:", error);
      setSnackbarMessage(t('remaining.personnelDetailModal.messages.personnelUpdateError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const renderListItem = (icon: React.ReactNode, primary: string, secondary: string | React.ReactNode | undefined, valueToEdit?: string, onChangeEdit?: (val: string) => void, type: string = "text", multiline: boolean = false) => {
    // Log ekleyelim - hangi değerin render edildiğini görelim
    console.log(`[PersonnelDetailsModal] renderListItem: primary=${primary}, secondary=${secondary || 'Belirtilmemiş'}, valueToEdit=${valueToEdit}`);
    
    // Sorun: secondary değeri undefined veya boş string olduğunda "Belirtilmemiş" gösteriliyor
    // Eğer düzenleme modunda bir değer var ama normal modda gösterilmiyorsa, valueToEdit'ten alabiliriz
    let displayValue = secondary;
    if ((!secondary || secondary === '') && valueToEdit) {
      displayValue = valueToEdit;
      console.log(`[PersonnelDetailsModal] Değer görüntüleme düzeltildi: ${primary} için boş değer yerine ${valueToEdit} kullanılıyor`);
    }
    
    return (
    <ListItem disablePadding sx={{mb: isEditMode ? 2 : 1.5}}>
        <ListItemIcon sx={{minWidth: 40, alignSelf: 'flex-start', mt: isEditMode? 1.5 : 0.5}}>{icon}</ListItemIcon>
        {isEditMode && valueToEdit !== undefined && onChangeEdit ? (
             <TextField 
                fullWidth 
                label={primary} 
                value={valueToEdit} 
                onChange={(e) => onChangeEdit(e.target.value)} 
                variant="outlined" 
                size="small" 
                type={type}
                multiline={multiline}
                rows={multiline ? 3 : 1}
            />
        ) : (
            <ListItemText 
                primary={<Typography variant="body2" color="text.secondary">{primary}</Typography>} 
                  secondary={<Typography variant="body1" fontWeight={500}>{displayValue || t('remaining.personnelDetailModal.common.unspecified')}</Typography>} 
            />
        )}
    </ListItem>
  );
  };

  return (
    <StyledModal
      open={open}
      onClose={() => {setIsEditMode(false); onClose();}}
      aria-labelledby="personel-detay-modal"
    >
      <ModalContent>
        {selectedPersonnel && (
          <>
            <ModalHeader>
              <Typography variant="h5" fontWeight="bold">
                {t('remaining.personnelDetailModal.title')}
              </Typography>
              <Box>
                {isEditMode ? (
                  <>
                    <Button 
                      onClick={handleEditToggle} 
                      startIcon={<CancelIcon />} 
                      sx={{mr: 1}}
                      color="inherit"
                      disabled={saveLoading}
                    >
                      {t('remaining.personnelPage.k023')}
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSave} 
                      startIcon={saveLoading ? <CircularProgress size={18} color="inherit"/> : <SaveIcon />} 
                      disabled={saveLoading}
                    >
                      {t('remaining.personnelDetailModal.actions.save')}
                    </Button>
                  </>
                ) : (
                  <Button variant="outlined" onClick={handleEditToggle} startIcon={<EditIcon />}>
                    {t('remaining.personnelDetailModal.actions.edit')}
                  </Button>
                )}
                <IconButton onClick={() => {setIsEditMode(false); onClose();}} sx={{ml: 1}} disabled={saveLoading}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </ModalHeader>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Stack spacing={2.5}>
                    <ModalSection sx={{ textAlign: 'center' }}>
                    {isImageLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 106, height: 106, mb: 1.5, mx: 'auto' }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Avatar 
                          src={profileImageUrl || undefined}
                          sx={{ 
                          width: 100, 
                          height: 100, 
                          bgcolor: 'primary.main',
                          fontSize: 40,
                          mb: 1.5,
                          mx: 'auto',
                          border: '3px solid',
                          borderColor: 'primary.light'
                          }}
                      >
                          {!profileImageUrl && (selectedPersonnel.name?.substring(0, 1).toUpperCase() || 'P')}
                      </Avatar>
                    )}
                    {isEditMode ? (
                        <TextField
                        fullWidth
                        label={t('remaining.personnelPage.k001')}
                        value={editableName}
                        onChange={(e) => setEditableName(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{mb:1}}
                        />
                    ) : (
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {selectedPersonnel.name}
                        </Typography>
                    )}
                    {!showDeleted && (
                        <Chip
                        label={selectedPersonnel.hasTask ? t('remaining.personnelPage.k055') : t('remaining.personnelPage.k056')}
                        size="small"
                        color={selectedPersonnel.hasTask ? 'primary' : 'success'}
                        sx={{ fontWeight: 'medium', mt: 0.5, mb:1 }}
                        />
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t('remaining.personnelDetailModal.labels.personnelId')}: {selectedPersonnel.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t('remaining.personnelPage.k008')}: {selectedPersonnel.role === 'employee' ? t('remaining.personnelPage.k047') : (selectedPersonnel.role || t('remaining.personnelDetailModal.common.unspecified'))}
                    </Typography>
                    </ModalSection>

                    {!showDeleted && !isManager && (
                    <ModalSection>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                            {t('remaining.personnelDetailModal.branchOps.title')}
                        </Typography>
                        <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        startIcon={<BusinessIcon />}
                        onClick={handleOpenBranchTransferModal}
                        sx={{ borderRadius: 1.5, mb: selectedPersonnel.branchesId ? 1 : 0, textTransform:'none', py:1 }}
                        >
                        {t('remaining.personnelDetailModal.branchOps.transferOrChange')}
                        </Button>
                        {selectedPersonnel.branchesId && (
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteForeverIcon />}
                            onClick={handleOpenRemoveDialog}
                            disabled={removingFromBranch}
                            sx={{ borderRadius: 1.5, mt:1, textTransform:'none', py:1 }}
                        >
                            {removingFromBranch ? (
                            <CircularProgress size={16} color="inherit" />
                            ) : (
                            t('remaining.personnelDetailModal.branchOps.removeAssignment')
                            )}
                        </Button>
                        )}
                    </ModalSection>
                    )}
                     {!showDeleted && (
                        <ModalSection>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                                {t('remaining.personnelDetailModal.deleteSection.title')}
                            </Typography>
                             {!confirmDelete ? (
                                <Button
                                fullWidth
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={onDeleteConfirm}
                                sx={{ borderRadius: 1.5, textTransform:'none', py:1 }}
                                >
                                {t('remaining.personnelDetailModal.deleteSection.deletePersonnel')}
                                </Button>
                            ) : (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                    onClick={onCancelDelete}
                                    sx={{ borderRadius: 1.5, textTransform:'none', py:0.8 }}
                                >
                                    {t('remaining.personnelPage.k023')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={onDeletePersonnel}
                                    sx={{ borderRadius: 1.5, textTransform:'none', py:0.8 }}
                                >
                                    {t('remaining.personnelDetailModal.deleteSection.confirmDelete')}
                                </Button>
                                </Stack>
                            )}
                        </ModalSection>
                    )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={4.5}>
                 <Stack spacing={2.5}>
                    <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                        {t('remaining.personnelDetailModal.sections.contactInfo')}
                    </Typography>
                    <List dense disablePadding>
                        {/* Email = Firebase Auth kimligi → DEGISTIRILEMEZ (salt-okunur, edit modunda da TextField yok). */}
                        {renderListItem(<EmailIcon color="action"/>, t('remaining.personnelPage.k005'), editableEmail || selectedPersonnel.email)}
                        {renderListItem(<PhoneIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelPage.k004'), editablePhone || selectedPersonnel.phone, editablePhone, setEditablePhone, "tel")}
                    </List>
                    </ModalSection>

                    <ModalSection>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                        {t('remaining.personnelDetailModal.sections.companyInfo')}
                    </Typography>
                    <List dense disablePadding>
                        <ListItem disablePadding sx={{mb: isEditMode ? 2 : 1.5}}>
                            <ListItemIcon sx={{minWidth: 40, alignSelf: 'flex-start', mt: isEditMode? 1.5 : 0.5}}><BusinessIcon color={isEditMode && !selectedPersonnel.branchesId ? "disabled" : (isEditMode ? "primary" : "action")} /></ListItemIcon>
                            {isEditMode && !isManager ? (
                                 <Typography variant="body2" color="textSecondary" sx={{flexGrow: 1, alignSelf: 'center'}}>
                                    {t('remaining.personnelPage.k091')}: {branchName || t('remaining.personnelDetailModal.branchOps.unassigned')} ({t('remaining.personnelDetailModal.branchOps.useLeftPanel')})
                                </Typography>
                            ) : (
                                 <ListItemText 
                                    primary={<Typography variant="body2" color="text.secondary">{t('remaining.personnelPage.k091')}</Typography>} 
                                    secondary={<Typography variant="body1" fontWeight={500}>{branchName || t('remaining.personnelDetailModal.branchOps.unassigned')}</Typography>} 
                                />
                            )}
                        </ListItem>
                        <ListItem disablePadding sx={{mb: isEditMode ? 2 : 1.5}}>
                            <ListItemIcon sx={{minWidth: 40, alignSelf: 'flex-start', mt: isEditMode? 1.5 : 0.5}}><CorporateFareIcon color={isEditMode ? "primary" : "action"}/></ListItemIcon>
                            {isEditMode ? (
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={departments}
                                    getOptionLabel={(option) => option.name}
                                    value={departments.find(dept => dept.id === editableDepartmentId) || null}
                                    onChange={(event, newValue) => {
                                      console.log('[PersonnelDetailsModal] Department selected:', newValue);
                                    setEditableDepartmentId(newValue ? newValue.id : null);
                                      if (newValue) {
                                        setDepartmentName(newValue.name);
                                      }
                                    }}
                                    loading={loadingDepartmentsForSelect}
                                    renderOption={(props, option) => (
                                      <li {...props} key={option.id}>
                                        <CorporateFareIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                                        {option.name}
                                      </li>
                                    )}
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('remaining.personnelPage.k002')}
                                        variant="outlined"
                                        InputProps={{
                                        ...params.InputProps,
                                          startAdornment: (
                                            <React.Fragment>
                                              <CorporateFareIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                              {params.InputProps.startAdornment}
                                            </React.Fragment>
                                          ),
                                        endAdornment: (
                                            <React.Fragment>
                                            {loadingDepartmentsForSelect ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                        }}
                                    />
                                    )}
                                />
                            ) : (
                                 <ListItemText 
                                    primary={<Typography variant="body2" color="text.secondary">{t('remaining.personnelPage.k002')}</Typography>} 
                                    secondary={<Typography variant="body1" fontWeight={500}>{loadingDepartment ? <CircularProgress size={16}/> : (departmentName || t('remaining.personnelDetailModal.common.unspecified'))}</Typography>} 
                                />
                            )}
                        </ListItem>
                        {renderListItem(<WorkIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelPage.k003'), editableJobTitle || (currentCompanyData?.jobTitle), editableJobTitle, setEditableJobTitle)}
                        {renderListItem(<CalendarTodayIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.startDate'), 
                          (editableStartDate && editableStartDate !== '') ? new Date(editableStartDate).toLocaleDateString(undefined) : 
                          (selectedPersonnel.startDate ? new Date(selectedPersonnel.startDate).toLocaleDateString(undefined) : undefined), 
                          editableStartDate, setEditableStartDate, "date")}
                    </List>
                    </ModalSection>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4.5}>
                <Stack spacing={2.5}>
                    <ModalSection>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {t('remaining.personnelDetailModal.sections.financialSummary', 'Mali Özet')}
                          </Typography>
                          {onOpenTransactions !== null && (
                            <Button
                              size="small"
                              endIcon={<ArrowForwardIcon fontSize="small" />}
                              onClick={() => {
                                if (onOpenTransactions) {
                                  onOpenTransactions(selectedPersonnel.id);
                                  return;
                                }
                                onClose();
                                navigate(`/personnel/actions/${selectedPersonnel.id}`);
                              }}
                            >
                              {t('remaining.personnelDetailModal.labels.viewTransactions', 'Hareketler')}
                            </Button>
                          )}
                        </Box>
                        {loadingBalances ? (
                          <CircularProgress size={20} />
                        ) : balances.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('remaining.personnelDetailModal.labels.noBalance', 'Bakiye hareketi yok')}
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {balances.map((b) => (
                              <Box key={b.currency} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <WalletIcon color="action" fontSize="small" />
                                  <Typography variant="body2" color="text.secondary">{b.currency}</Typography>
                                </Box>
                                {/* Pozitif = şirket alacaklı, negatif = şirket borçlu.
                                    Personel listesindeki bakiye çipiyle aynı yorum. */}
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={b.balance > 0 ? 'success.main' : b.balance < 0 ? 'error.main' : 'text.primary'}
                                >
                                  {b.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                    </ModalSection>

                    <ModalSection>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                          {t('remaining.personnelDetailModal.sections.cards', 'Kartlar')}
                        </Typography>
                        {loadingCards ? (
                          <CircularProgress size={20} />
                        ) : personnelCards.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('remaining.personnelDetailModal.labels.noCards', 'Bu personele bağlı kart yok')}
                          </Typography>
                        ) : (
                          <Stack spacing={1.25}>
                            {personnelCards.map((card: any) => (
                              <Box key={card.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CreditCardIcon color="action" fontSize="small" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
                                    {card.cardNumber}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {card.cardTypeName || '—'}
                                    {card.activeSession?.status === 'ACTIVE'
                                      ? ` · ${t('remaining.personnelDetailModal.labels.sessionOpen', 'Oturum açık')}`
                                      : ''}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {(card.totalBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                                {!card.isActive && (
                                  <Chip label={t('remaining.personnelDetailModal.labels.cardInactive', 'Pasif')} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                    </ModalSection>

                    <ModalSection>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>{t('remaining.personnelDetailModal.sections.personalInfo')}</Typography>
                        <List dense disablePadding>
                            {renderListItem(<CalendarTodayIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.birthDate'), 
                              (editableBirthDate && editableBirthDate !== '') ? new Date(editableBirthDate).toLocaleDateString(undefined) : 
                              (selectedPersonnel.birthDate ? new Date(selectedPersonnel.birthDate).toLocaleDateString(undefined) : undefined), 
                              editableBirthDate, setEditableBirthDate, "date")}
                            {renderListItem(<HomeIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.address'), editableAddress || (currentCompanyData?.address), editableAddress, setEditableAddress, "text", true)}
                        </List>
                    </ModalSection>

                    <ModalSection>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>{t('remaining.personnelDetailModal.sections.emergencyInfo')}</Typography>
                        <List dense disablePadding>
                             {renderListItem(<ContactPhoneIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.emergencyContactName'), editableEmergencyContactName || (currentCompanyData?.emergencyContactName), editableEmergencyContactName, setEditableEmergencyContactName)}
                             {renderListItem(<PhoneIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.emergencyContactPhone'), editableEmergencyContactPhone || (currentCompanyData?.emergencyContactPhone), editableEmergencyContactPhone, setEditableEmergencyContactPhone, "tel")}
                        </List>
                    </ModalSection>
                    
                    <ModalSection>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>{t('remaining.personnelDetailModal.sections.extraNotes')}</Typography>
                         {renderListItem(<NotesIcon color={isEditMode ? "primary" : "action"}/>, t('remaining.personnelDetailModal.labels.notes'), editableNotes || (currentCompanyData?.notes), editableNotes, setEditableNotes, "text", true)}
                    </ModalSection>
                </Stack>
              </Grid>
            </Grid>
            
            <PersonnelBranchTransferModal
              open={branchTransferModalOpen}
              onClose={handleCloseBranchTransferModal}
              personnelId={selectedPersonnel.id}
              personnelName={selectedPersonnel.name}
              currentBranchId={selectedPersonnel.branchesId}
              companyId={selectedPersonnel.companyId}
              onBranchTransferred={() => {
                if (onPersonnelUpdated) onPersonnelUpdated();
                if (selectedPersonnel?.branchesId) loadBranchName(selectedPersonnel.branchesId);
              }}
            />

            <Dialog
              open={confirmRemoveDialogOpen}
              onClose={handleCloseRemoveDialog}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                {t('remaining.personnelDetailModal.branchOps.removeAssignment')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t('remaining.personnelDetailModal.branchOps.removeAssignmentConfirm', { personnelName: selectedPersonnel.name, branchName })}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseRemoveDialog} color="inherit">
                  {t('remaining.personnelPage.k023')}
                </Button>
                <Button onClick={handleRemoveBranchAssignment} color="error" variant="contained" autoFocus disabled={removingFromBranch}>
                  {removingFromBranch ? <CircularProgress size={18} color="inherit"/> : t('remaining.personnelDetailModal.branchOps.removeFromBranch')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSnackbarOpen(false)} 
              severity={snackbarSeverity} 
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
      </ModalContent>
    </StyledModal>
  );
};

export default PersonnelDetailsModal; 